import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  /** Duration in ms. 0 = persist until dismissed. Default: 4000 */
  duration?: number;
}

export interface ConfirmOptions {
  title: string;
  message?: string;
  /** Label/value pairs displayed in a subtle info box — e.g. { Employee: 'Solange', Date: '01.04.2026' } */
  entityInfo?: Record<string, string>;
  /** Renders a red confirm button and warning icon. Default: false. */
  danger?: boolean;
  /** Override confirm button label. Default: t('btn.confirm') */
  confirmText?: string;
  /** Override cancel button label. Default: t('btn.cancel') */
  cancelText?: string;
}

interface UIState {
  toasts: Toast[];
  /** ID of the currently open modal, or null */
  activeModal: string | null;
  /** Pending confirmation request, or null when no dialog is open */
  confirmRequest: { options: ConfirmOptions; resolve: (result: boolean) => void } | null;

  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  openModal: (id: string) => void;
  closeModal: () => void;
  requestConfirm: (options: ConfirmOptions) => Promise<boolean>;
  resolveConfirm: (result: boolean) => void;
}

let toastCounter = 0;

export const useUIStore = create<UIState>((set, get) => ({
  toasts: [],
  activeModal: null,
  confirmRequest: null,

  addToast(toast) {
    const id = `toast-${++toastCounter}`;
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }));

    const duration = toast.duration ?? 4000;
    if (duration > 0) {
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      }, duration);
    }
  },

  removeToast(id) {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },

  openModal(id) {
    set({ activeModal: id });
  },

  closeModal() {
    set({ activeModal: null });
  },

  requestConfirm(options) {
    return new Promise<boolean>((resolve) => {
      set({ confirmRequest: { options, resolve } });
    });
  },

  resolveConfirm(result) {
    const { confirmRequest } = get();
    if (confirmRequest) {
      confirmRequest.resolve(result);
      set({ confirmRequest: null });
    }
  },
}));

/** Convenience hook — returns just the addToast action */
export function useToast() {
  return useUIStore((s) => s.addToast);
}

/** Convenience hook — returns requestConfirm, a Promise<boolean>-returning function */
export function useConfirm() {
  return useUIStore((s) => s.requestConfirm);
}
